import React, {FC, useEffect, useRef, useState} from 'react';
import {InputAdornment, TextField} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {useTranslation} from 'react-i18next';

/** Debounce delay in milliseconds before triggering the search callback. */
const DEBOUNCE_DELAY_MS = 300;

export interface PalowanVacationSearchBoxProps {
  /** Callback invoked with the current search term after the debounce delay. */
  onSearch: (searchTerm: string) => void;
  /** When true the input is disabled to prevent new searches while loading. */
  isLoading?: boolean;
}

/**
 * Debounced text input for searching palowans by name within the vacation assignment view.
 * The onSearch callback fires 300 ms after the user stops typing to avoid flooding the API.
 *
 * @param onSearch - Receives the debounced search string.
 * @param isLoading - Disables the field when a query is in flight.
 * @returns A MUI TextField with a leading search icon.
 */
const PalowanVacationSearchBox: FC<PalowanVacationSearchBoxProps> = ({
  onSearch,
  isLoading = false,
}) => {
  const {t} = useTranslation();
  const [value, setValue] = useState('');
  // Ref keeps the timer ID across renders without triggering re-renders
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setValue(newValue);

    // Clear any pending debounce so only the latest input fires the callback
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      onSearch(newValue);
    }, DEBOUNCE_DELAY_MS);
  };

  // Clean up the timer if the component unmounts while a debounce is pending
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <TextField
      value={value}
      onChange={handleChange}
      disabled={isLoading}
      placeholder={t('leaves.PalowanVacationSearchBox.placeholder')}
      inputProps={{
        'aria-label': t('leaves.PalowanVacationSearchBox.placeholder'),
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
      className="w-full max-w-sm"
    />
  );
};

export default PalowanVacationSearchBox;
